import {
  type Authorization,
  getAuthorizationService,
  type Permission,
  type Subscription,
} from '@art-basel/artbasel-lib-platform';
import { handleHttpError } from '@/utils/errorHandlers';
import { defineStore } from 'pinia';
import { ref } from 'vue';

export const AuthorizationStoreName = 'AuthorizationStore';
export const useAuthorizationStore = defineStore(AuthorizationStoreName, () => {
  const authorizations = ref<Authorization | null>(null);

  const initAuthorizations = async (refresh = false) => {
    if (!authorizations.value || refresh) {
      await getAuthorizationService()
        .getAuthorizations()
        .then((response) => {
          authorizations.value = response;
        })
        .catch((e) => {
          handleHttpError(e);
          authorizations.value = null;
        });
    }
  };

  /**
   * Determine is the user has any permissions, i.e. they are allowed to access the gallery portal.
   * TODO: re-enable once the invitation issue is resolved
   * () => authorizations.value?.permissions && authorizations.value?.permissions.length > 0;
   */
  const isAppAuthorized = () => true;

  /**
   * Check if the user has a specific subscription
   * @param subscription the subscription to check
   */
  const hasSubscription = (subscription: Subscription): boolean => {
    return !!authorizations.value?.subscriptions.includes(subscription);
  };

  /**
   * Check if the user has a specific permission
   * @param permission the permission to check
   */
  const hasPermission = (permission: Permission): boolean => {
    return !!authorizations.value?.permissions.includes(permission);
  };

  /**
   * Check if the user has any one of the permissions
   * @param permissions the permissions to check
   */
  const hasAnyPermission = (...permissions: Array<Permission>): boolean => {
    return permissions.some(hasPermission);
  };

  /**
   * Check if the user has any one of the permissions
   * @param permissions the permissions to check
   */
  const hasAllPermissions = (...permissions: Array<Permission>): boolean => {
    return permissions.every(hasPermission);
  };

  /**
   * Reset the store to its initial state
   */
  const reset = () => {
    authorizations.value = null;
  };

  return {
    initAuthorizations,
    hasSubscription,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isAppAuthorized,
    reset,
  };
});
